// Domains
import React, { useMemo } from 'react'

import { STATSIG_EXPERIMENTS, useStatsigExperimentBucket } from '@src/experiments/statsig-integration'
import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import Button from '@src/quince-site-common/src/domains/Button'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import Image from '@src/quince-site-common/src/domains/Image'
import Link from '@src/quince-site-common/src/domains/Link'
import RichText from '@src/quince-site-common/src/domains/RichText'
import Video from '@src/quince-site-common/src/domains/Video'
import useDynamicStyle from '@src/quince-site-common/src/hooks/useDynamicStyle'
import filterFalsy from '@src/quince-site-common/src/utils/filterFalsy'
import { ButtonCTAType, CONTAINER_CTA_B2B_SELF_SERVE_FLAG } from '@src/types/button-cta.type'
import { navigateToB2BSelfServe } from '@src/utils/navigate-to-b2b'

import { ContentWithBackgroundImageProps } from '../ContentWithBackgroundImage'

const useContentWithBackgroundImageProps: (customSection: CustomSection) => ContentWithBackgroundImageProps = (
  customSection: CustomSection,
) => {
  const {
    richTexts = [],
    buttons = [],
    images = [],
    videos = [],
    layoutType = LAYOUT_TYPES.DEFAULT,
    containerLinks = [],
    classNames = [],
    classNamesMobileOverrides = [],
    baseDynamicClasses = [],
    mobileDynamicClassesOverrides = [],
    tracker,
    trackingId,
    trackingMetaData,
    trackingTypes,
    elementPosition,
    displayTitle,
    contentContainerName,
    contentContainerType,
    runExperiment,
    experimentName,
  } = customSection

  const isBannerExperiment = experimentName === STATSIG_EXPERIMENTS.BANNER_A_CLP_A_V_A_EXPERIMENT

  const experimentBucketName = useStatsigExperimentBucket(
    isBannerExperiment ? STATSIG_EXPERIMENTS.BANNER_A_CLP_A_V_A_EXPERIMENT : null,
  )

  const link = getNavigationLink({
    runExperiment: !!runExperiment,
    experimentBucketName,
    containerLinks,
  })

  // When flagged, a whole-image/container click routes through navigateToB2BSelfServe (D2C→B2B
  // token transfer) using the container link's URL, instead of a plain navigation.
  const isContainerB2BSelfServe = [...classNames, ...baseDynamicClasses].includes(
    CONTAINER_CTA_B2B_SELF_SERVE_FLAG,
  )

  const { className, styleObject } = useDynamicStyle({
    mobileClassNamesOverride: classNamesMobileOverrides,
    baseClassNames: classNames,
    baseDynamicValueClasses: baseDynamicClasses,
    dynamicValueClassesMobileOverride: mobileDynamicClassesOverrides,
    wrapperClassName: experimentBucketName ?? '',
    // eslint-disable-next-line i18next/no-literal-string
    postFix: 'content-with-background-image',
  })

  return useMemo(() => {
    const titleRichText = richTexts[0]
    const descriptionRichText = richTexts[1]
    const imageProps = Image.makeImageProps(images[0])
    const videoProps = Video.makeVideoProps(videos[0])

    imageProps.quality = 95
    return {
      titleRichTextProps: RichText.makeRichTextProps(titleRichText),
      descriptionRichTextProps: RichText.makeRichTextProps(descriptionRichText),
      richTextsItems: richTexts.map(richText => RichText.makeRichTextProps(richText)),
      buttonPropsList: filterFalsy(
        buttons.map(button => {
          const props = Button.makeButtonProps(button)
          if (!props) return props
          if (button.ctaType === ButtonCTAType.B2B_SELF_SERVE) {
            const contentfulUrl =
              (button.link as (typeof button.link & { url?: string }) | undefined)?.url ?? button.link?.navigationPath
            if (!contentfulUrl) return props
            return {
              ...props,
              href: undefined,
              onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                navigateToB2BSelfServe(contentfulUrl)
              },
            }
          }
          return props
        }),
      ),
      imageProps: imageProps,
      hasButton: !!buttons.length,
      layoutType,
      videoProps,
      link,
      isContainerB2BSelfServe,
      className,
      styleObject,
      tracker,
      trackingId,
      trackingMetaData,
      trackingTypes,
      elementPosition,
      displayTitle,
      contentContainerName,
      contentContainerType,
    }
  }, [
    buttons,
    className,
    images,
    layoutType,
    richTexts,
    styleObject,
    videos,
    tracker,
    trackingId,
    trackingMetaData,
    trackingTypes,
    elementPosition,
    displayTitle,
    contentContainerName,
    contentContainerType,
    link,
    isContainerB2BSelfServe,
  ])
}

const getNavigationLink = ({
  runExperiment,
  experimentBucketName,
  containerLinks,
}: {
  runExperiment: boolean
  experimentBucketName: string | null | undefined
  containerLinks: Link[]
}) => {
  if (!runExperiment || !experimentBucketName) {
    return containerLinks[0]
  }

  const linkForBucket = containerLinks.find((containerLink): boolean => {
    const { experimentConfig: experimentConfigs } = containerLink
    if (!experimentConfigs) {
      return false
    }

    const experimentConfigForStatsigExp = experimentConfigs.find(
      config => config.experimentName === (STATSIG_EXPERIMENTS.BANNER_A_CLP_A_V_A_EXPERIMENT as string),
    )

    const variantForLink = experimentConfigForStatsigExp?.experimentVariant
    return variantForLink === experimentBucketName
  })

  return linkForBucket ?? containerLinks[0]
}

export default useContentWithBackgroundImageProps
